import { Skeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";

import { ReportCellResult, ReportMedia } from "@megaron/crm-contracts";

import { OutputTable } from "./OutputTable";

export const hasOutput = (result: ReportCellResult) =>
  Boolean(result.stdout || result.stderr || result.error) || result.media.length > 0 || result.columns.length > 0;

export const CellResult: React.FC<{ result: ReportCellResult }> = ({ result }) => (
  <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: 0 }}>
    {result.stdout && <TextOutput text={result.stdout} />}
    {result.stderr && <TextOutput text={result.stderr} />}
    {result.error && <TextOutput text={result.error} isError />}
    {result.media.map((media, i) => (
      <MediaOutput key={i} media={media} />
    ))}
    {result.columns.length > 0 && <OutputTable columns={result.columns} rows={result.rows} />}
  </div>
);

const MediaOutput: React.FC<{ media: ReportMedia }> = ({ media }) =>
  media.type === "image/png" ? (
    <img alt="" src={`data:image/png;base64,${media.data}`} css={{ maxWidth: "100%" }} />
  ) : (
    <HtmlOutput html={media.data} />
  );

const TextOutput: React.FC<{ text: string; isError?: boolean }> = ({ text, isError }) => {
  const theme = useTheme();

  return (
    <pre
      css={{
        margin: 0,
        padding: "0.5rem 0.75rem",
        borderRadius: theme.smallBorderRadius,
        background: isError ? "rgba(192, 57, 43, 0.08)" : theme.colors.background,
        color: isError ? theme.colors.danger : "#1d1c1a",
        fontSize: "0.8125rem",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {text}
    </pre>
  );
};

const frameDocument = (html: string) => `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>body { margin: 0; }</style>
</head>
<body>
${html}
<script>
new ResizeObserver(() => parent.postMessage({ frameHeight: document.body.scrollHeight }, "*")).observe(document.body);
</script>
</body>
</html>`;

const HtmlOutput: React.FC<{ html: string }> = ({ html }) => {
  const theme = useTheme();
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent<{ frameHeight?: number }>) => {
      if (event.source === frame.current?.contentWindow && typeof event.data?.frameHeight === "number") {
        setHeight(event.data.frameHeight);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <div css={{ position: "relative" }}>
      <iframe
        ref={frame}
        title="Wynik HTML"
        sandbox="allow-scripts"
        srcDoc={frameDocument(html)}
        css={{ width: "100%", height: `${height ?? 100}px`, border: "none", display: "block" }}
      />
      {height === null && (
        <Skeleton
          height="100%"
          css={{ position: "absolute", inset: 0, display: "block", borderRadius: theme.smallBorderRadius }}
        />
      )}
    </div>
  );
};
